import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FaBookOpen, FaSyncAlt } from 'react-icons/fa';
import AdminNav from '../components/admin/AdminNav';
import Button from '../components/common/Button';
import Heading from '../components/common/Heading';
import Section from '../components/common/Section';
import { fetchAdminPrograms } from '../services/registrationService';
import { clearAdminToken, getAdminToken } from '../utils/adminAuth';

const formatMoney = (value) => `NLe ${Number(value || 0).toLocaleString('en')}`;

const AdminProgramsPage = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadPrograms = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await fetchAdminPrograms(getAdminToken());
      setPrograms(data);
      setHasLoaded(true);
    } catch (requestError) {
      if (requestError.response?.status === 401) {
        clearAdminToken();
      }
      setError(requestError.response?.data?.message || 'Unable to load programs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>Admin Programs - Afrovate Tech Hub</title>
      </Helmet>

      <section className="bg-primary-dark pt-32 pb-16 text-white">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Program Payments</h1>
          <p className="text-white text-opacity-90 text-lg">View registration and payment counts for every training program.</p>
        </div>
      </section>

      <Section background="white" padding="lg">
        <AdminNav />

        <div className="flex justify-end mb-6">
          <Button variant="outline-primary" onClick={loadPrograms} disabled={loading} icon={<FaSyncAlt className={loading ? 'animate-spin' : ''} />}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
        {error && <p className="text-red-600 mb-6">{error}</p>}

        <div className="flex items-center justify-between mb-4">
          <Heading level={2}>Programs</Heading>
          <p className="text-gray-600">{programs.length} program{programs.length !== 1 ? 's' : ''}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {programs.map((program) => (
            <Link
              key={program.id}
              to={`/admin/programs/${program.id}`}
              className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm hover:border-primary transition-colors"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-primary-dark">{program.title}</h3>
                  <p className="text-sm text-gray-500">{formatMoney(program.amount)}</p>
                </div>
                <FaBookOpen className="text-primary text-2xl" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-xs text-gray-500">Registered</p>
                  <p className="text-2xl font-bold text-primary-dark">{program.registered_count}</p>
                </div>
                <div className="bg-green-50 rounded-md p-3">
                  <p className="text-xs text-gray-500">Paid</p>
                  <p className="text-2xl font-bold text-green-700">{program.paid_count}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {hasLoaded && programs.length === 0 && (
          <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">No programs found.</div>
        )}
      </Section>
    </>
  );
};

export default AdminProgramsPage;
